/**
 * VisitScheduleMatrix — checkbox grid for assigning CRF forms to study visits.
 *
 * Reads visits from studyDesign/config (real-time) and existing mapping from
 * crfPackage/visitMapping (one-time), then lets users toggle form-visit
 * assignments, save, and detect protocol amendments.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getDoc,
  setDoc,
  onSnapshot,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { saveAs } from 'file-saver';
import { ownWindowText } from './visitMappingWindow';
import {
  planSeed,
  shouldStartMatch,
  countTicks,
  suggestAssignmentsFromSchedule,
  wordingMatchedCells,
} from './seedVisitAssignments';
import { formsToConfirmSummary } from './formsToConfirmSummary';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import {
  studyDesignConfigDoc,
  scheduleConfirmationDoc,
  crfPackageVisitMappingDoc,
  protocolAnalysisLatestDoc,
  artifactDoc,
} from '../../constants/firestorePaths';
import { readDocVersion, docChangedMessage } from '../../utils/versionedDoc';
import { versionedDocFor } from '../../utils/versionedFirestoreDoc';
import {
  confirmationStatus,
  type ConfirmationVerdict,
  type ScheduleConfirmationRecordLike,
} from '../../utils/scheduleConfirmationStatus';
import {
  Loader2,
  Save,
  Check,
  AlertTriangle,
  Info,
  Wand2,
  X,
  RefreshCw,
  Download,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────

interface VisitScheduleMatrixProps {
  projectId: string;
  forms: { id: string; name: string; category: string }[];
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface Visit {
  id: string;
  name: string;
  /** null = the protocol states no study day for this visit. */
  day: number | null;
  windowBefore: number;
  windowAfter: number;
  /** The visit's own window/timing text, when it has one. */
  window?: string;
  timing?: string;
  required: boolean;
  forms: string[];
  assessments?: string[];
  formsToConfirm?: string[];
  formMatches?: Array<{ label: string; form: string; how: string; item?: string }>;
}

interface VisitMappingDoc {
  visits: {
    id: string;
    name: string;
    window?: string;
    forms: string[];
  }[];
  form_inventory: {
    id: string;
    name: string;
    layout: string;
    category: string;
  }[];
  study?: any;
  editedAt?: any;
  editedBy?: string;
  protocolAnalysisTimestamp?: any;
  generatedAt?: any;
}

// Category sort order for grouping rows
const CATEGORY_ORDER: string[] = [
  'one-time',
  'every-visit',
  'periodic',
  'as-needed',
  'screening',
  'demographics',
  'medical_history',
  'procedures',
  'outcomes',
  'safety',
  'administrative',
  'other',
];

const CATEGORY_LABELS: Record<string, string> = {
  'one-time': 'One-Time',
  'every-visit': 'Every Visit',
  'periodic': 'Periodic',
  'as-needed': 'As Needed',
  'screening': 'Screening',
  'demographics': 'Demographics',
  'medical_history': 'Medical History',
  'procedures': 'Procedures',
  'outcomes': 'Outcomes',
  'safety': 'Safety',
  'administrative': 'Administrative',
  'other': 'Other',
};

// ─── Helpers ───────────────────────────────────────────────

/** Convert a Firestore timestamp (or Date) to a Date, safely. */
function toDate(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') return new Date(v);
  return null;
}

/** Format a Date for display, e.g. "Apr 11" */
function shortDate(d: Date | null): string {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Serialize assignments Map<formId, Set<visitId>> to a comparable string for dirty tracking. */
function serializeAssignments(m: Map<string, Set<string>>): string {
  const entries = Array.from(m.entries())
    .map(([k, v]) => `${k}:${Array.from(v).sort().join(',')}`)
    .sort();
  return entries.join('|');
}

// ─── Component ─────────────────────────────────────────────

export default function VisitScheduleMatrix({
  projectId,
  forms,
  showToast,
}: VisitScheduleMatrixProps) {
  // State
  const [visits, setVisits] = useState<Visit[]>([]);
  const [visitsLoaded, setVisitsLoaded] = useState(false);
  // Matched CRF form names in the exact row order of the uploaded Schedule of
  // Events. Drives the matrix row order so it mirrors the source SOE.
  const [scheduleFormOrder, setScheduleFormOrder] = useState<string[]>([]);
  const [assignments, setAssignments] = useState<Map<string, Set<string>>>(new Map());
  const [savedMapping, setSavedMapping] = useState<VisitMappingDoc | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [mappingProbe, setMappingProbe] = useState<{ projectId: string; exists: boolean; ticks: number } | null>(null);
  const matchStartedRef = useRef<string | null>(null);
  const seededRef = useRef<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Optimistic concurrency: the version of crfPackage/visitMapping whose
  // assignments this page holds.
  const mappingVersion = useMemo(
    () => (projectId ? versionedDocFor(crfPackageVisitMappingDoc(projectId)) : null),
    [projectId],
  );

  // Protocol amendment detection
  const [protocolStale, setProtocolStale] = useState(false);
  const [protocolDismissed, setProtocolDismissed] = useState(false);

  // Stale artifact banner
  const [staleArtifacts, setStaleArtifacts] = useState<{ name: string; date: string }[]>([]);

  // Schedule confirmation — who reviewed this schedule, and has it moved since
  // (coverage map G9). The record was written and drift-checked from the day it
  // was built; nothing displayed it, so nobody could see either answer.
  const [confirmation, setConfirmation] = useState<ScheduleConfirmationRecordLike | null>(null);
  const [confirmationLoaded, setConfirmationLoaded] = useState(false);
  const [confirmationVerdict, setConfirmationVerdict] = useState<ConfirmationVerdict | null>(null);

  // Bulk-action confirmation (row = all visits for a form; col = all forms for a visit)
  const [pendingBulk, setPendingBulk] = useState<{
    type: 'row' | 'col';
    id: string;
    label: string;
    allChecked: boolean;
    count: number;
  } | null>(null);

  // ── Load visits from studyDesign/config (real-time) ────

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      studyDesignConfigDoc(projectId),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setVisits((data.visits as Visit[]) || []);
          setScheduleFormOrder(
            Array.isArray(data.scheduleFormOrder)
              ? (data.scheduleFormOrder as string[])
              : [],
          );
        } else {
          setVisits([]);
          setScheduleFormOrder([]);
        }
        setVisitsLoaded(true);
      },
      (err) => {
        console.error('VisitScheduleMatrix: error loading visits', err);
        setVisits([]);
        setVisitsLoaded(true);
      },
    );
    return () => unsub();
  }, [projectId]);

  // ── Schedule confirmation record (real-time) ────
  //
  // Read-only. Confirming is `confirmScheduleV2`'s job; this only reports what
  // the record says. A missing doc is UNCONFIRMED, not an error — most studies
  // predate the record and must read honestly rather than blank.
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      scheduleConfirmationDoc(projectId),
      (snap) => {
        setConfirmation(snap.exists() ? (snap.data() as ScheduleConfirmationRecordLike) : null);
        setConfirmationLoaded(true);
      },
      (err) => {
        console.error('VisitScheduleMatrix: error loading schedule confirmation', err);
        setConfirmation(null);
        setConfirmationLoaded(true);
      },
    );
    return () => unsub();
  }, [projectId]);

  // The verdict compares a fingerprint, and the browser's SHA-256 is async — so
  // it is computed in an effect rather than during render. `cancelled` guards
  // the case where the schedule changes again before the hash resolves, which
  // would otherwise let an older verdict overwrite a newer one.
  useEffect(() => {
    if (!visitsLoaded || !confirmationLoaded) return;
    let cancelled = false;
    confirmationStatus(confirmation, visits as never)
      .then((v) => {
        if (!cancelled) setConfirmationVerdict(v);
      })
      .catch((err) => {
        // Never guess. A verdict that cannot be computed is not a pass.
        console.error('VisitScheduleMatrix: could not compute confirmation status', err);
        if (!cancelled) setConfirmationVerdict(null);
      });
    return () => {
      cancelled = true;
    };
  }, [visits, confirmation, visitsLoaded, confirmationLoaded]);

  // ── Load existing visitMapping (one-time) + seed assignments ────

  useEffect(() => {
    if (!projectId || !visitsLoaded) return;
    if (visits.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const mappingSnap = await getDoc(
          crfPackageVisitMappingDoc(projectId),
        );

        if (cancelled) return;

        const newAssignments = new Map<string, Set<string>>();
        // Ensure all forms have an entry
        forms.forEach((f) => newAssignments.set(f.id, new Set()));

        if (mappingSnap.exists()) {
          const data = mappingSnap.data() as VisitMappingDoc;
          // The assignments below are rebuilt from this copy, so the page
          // records its version.
          mappingVersion?.observe(readDocVersion(data));
          setSavedMapping(data);

          // Build assignments from saved mapping
          for (const v of data.visits || []) {
            for (const formId of v.forms || []) {
              if (newAssignments.has(formId)) {
                newAssignments.get(formId)!.add(v.id);
              } else {
                // Form may be in mapping but not in current forms list — still track it
                newAssignments.set(formId, new Set([v.id]));
              }
            }
          }

          // Protocol amendment detection
          if (data.protocolAnalysisTimestamp) {
            try {
              const analysisSnap = await getDoc(
                protocolAnalysisLatestDoc(projectId),
              );
              if (!cancelled && analysisSnap.exists()) {
                const analysisUpdated = toDate(analysisSnap.data().updatedAt);
                const mappingTs = toDate(data.protocolAnalysisTimestamp);
                if (analysisUpdated && mappingTs && analysisUpdated > mappingTs) {
                  setProtocolStale(true);
                }
              }
            } catch (e) {
              console.error('VisitScheduleMatrix: error checking protocol staleness', e);
            }
          }
        } else {
          setSavedMapping(null);
        }

        // Seeding is decided by the effect below, which re-asks whenever the
        // schedule or the form list changes. It is NOT done here: this run is
        // cancelled by any change in visits.length or forms.length, and the old
        // in-line seed (after a ~10 s callable) was therefore dropped every time.
        setMappingProbe({ projectId, exists: mappingSnap.exists(), ticks: countTicks(newAssignments) });
        if (!mappingSnap.exists() && shouldStartMatch(matchStartedRef.current, projectId)) {
          // Once per project open, and not tied to this run's cancellation. The
          // ONE strict matcher writes the matched names into studyDesign/config;
          // the page's own listener delivers them to the seed effect.
          matchStartedRef.current = projectId;
          httpsCallable(functions, CLOUD_FUNCTIONS.MATCH_SCHEDULE_FORMS, {
            timeout: CALLABLE_TIMEOUTS.MEDIUM,
          })({ projectId }).catch((e) => {
            console.error('VisitScheduleMatrix: schedule form matching failed; seeding from stored forms', e);
          });
        }

        if (!cancelled) {
          if (!mappingSnap.exists() && seededRef.current === projectId) {
            // Already seeded this open (and auto-saved): a re-run for a changed
            // forms list must not wipe the seed before the saved copy is readable.
            setAssignments((prev) => {
              const keep = new Map(prev);
              newAssignments.forEach((set, id) => { if (!keep.has(id)) keep.set(id, set); });
              return keep;
            });
          } else {
            setAssignments(newAssignments);
            setSavedSnapshot(serializeAssignments(newAssignments));
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('VisitScheduleMatrix: error loading mapping', err);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // We include forms.length so we re-run when forms list changes,
    // but we don't want to re-run on every forms reference change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, visitsLoaded, visits.length, forms.length]);

  // ── Seed an empty grid from the matched schedule ────
  //
  // Re-evaluated on every change of visits, forms or assignments, so it works
  // whichever of them arrives last (forms after visits, visits after the match).

  useEffect(() => {
    if (!projectId || loading || !mappingProbe || mappingProbe.projectId !== projectId) return;
    const plan = planSeed({
      mappingExists: mappingProbe.exists,
      savedTicks: mappingProbe.ticks,
      currentTicks: countTicks(assignments),
      seededAlready: seededRef.current === projectId,
      visits: visits as never,
      forms,
    });
    if (!plan.seed) return;
    seededRef.current = projectId;
    const merged = new Map(assignments);
    plan.assignments.forEach((set, id) => merged.set(id, set));
    setAssignments(merged);
    if (!plan.autoSave) return; // an existing (empty) mapping is never overwritten; the person can Save
    const seededDoc: VisitMappingDoc = {
      visits: visits.map((v) => ({
        id: v.id,
        name: v.name,
        ...(ownWindowText(v) !== undefined ? { window: ownWindowText(v) as string } : {}),
        forms: forms.filter((f) => merged.get(f.id)?.has(v.id)).map((f) => f.id),
      })),
      form_inventory: forms.map((f) => ({
        id: f.id,
        name: f.name,
        layout: 'question-list',
        category: f.category,
      })),
      editedAt: new Date(),
      editedBy: getAuth().currentUser?.uid || 'unknown',
    };
    setSavedMapping(seededDoc);
    setSavedSnapshot(serializeAssignments(merged));
    // Claims the stored version plus one, and is refused if another page seeded
    // or saved a mapping first.
    mappingVersion
      ?.write((next) => setDoc(crfPackageVisitMappingDoc(projectId), { ...seededDoc, docVersion: next }))
      .then((outcome) => {
        if (outcome !== 'saved') showToast(docChangedMessage('The form assignments'), 'error');
      })
      .catch((e) => {
        console.error('[VisitScheduleMatrix] auto-save seeded mapping failed', e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, loading, mappingProbe, visits, forms, assignments]);

  // ── Dirty tracking ────

  const isDirty = useMemo(
    () => serializeAssignments(assignments) !== savedSnapshot,
    [assignments, savedSnapshot],
  );

  const unsavedCount = useMemo(() => {
    if (!isDirty) return 0;
    // Count individual cell differences
    let count = 0;
    const currentSer = new Map<string, Set<string>>();
    // Parse saved snapshot back for comparison
    if (savedSnapshot) {
      for (const entry of savedSnapshot.split('|')) {
        const [key, vals] = entry.split(':');
        currentSer.set(key, new Set(vals ? vals.split(',').filter(Boolean) : []));
      }
    }
    for (const [formId, visitSet] of assignments) {
      const savedSet = currentSer.get(formId) || new Set<string>();
      for (const vid of visitSet) {
        if (!savedSet.has(vid)) count++;
      }
      for (const vid of savedSet) {
        if (!visitSet.has(vid)) count++;
      }
    }
    return count;
  }, [assignments, savedSnapshot]);

  // ── Toggle handlers ────

  const toggleCell = useCallback((formId: string, visitId: string) => {
    setAssignments((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(formId) || []);
      if (set.has(visitId)) {
        set.delete(visitId);
      } else {
        set.add(visitId);
      }
      next.set(formId, set);
      return next;
    });
  }, []);

  const toggleRow = useCallback(
    (formId: string) => {
      setAssignments((prev) => {
        const next = new Map(prev);
        const current = next.get(formId) || new Set<string>();
        // If all visits checked, uncheck all; otherwise check all
        const allChecked = visits.every((v) => current.has(v.id));
        next.set(formId, allChecked ? new Set() : new Set(visits.map((v) => v.id)));
        return next;
      });
    },
    [visits],
  );

  const toggleColumn = useCallback(
    (visitId: string) => {
      setAssignments((prev) => {
        const next = new Map(prev);
        // Check if all forms are checked for this visit
        const allChecked = forms.every((f) => (next.get(f.id) || new Set()).has(visitId));
        for (const f of forms) {
          const set = new Set(next.get(f.id) || []);
          if (allChecked) {
            set.delete(visitId);
          } else {
            set.add(visitId);
          }
          next.set(f.id, set);
        }
        return next;
      });
    },
    [forms],
  );

  const requestToggleRow = useCallback(
    (formId: string, formName: string) => {
      const current = assignments.get(formId) || new Set<string>();
      const allChecked = visits.every((v) => current.has(v.id));
      setPendingBulk({ type: 'row', id: formId, label: formName, allChecked, count: visits.length });
    },
    [assignments, visits],
  );

  const requestToggleColumn = useCallback(
    (visitId: string, visitName: string) => {
      const allChecked = forms.every((f) => (assignments.get(f.id) || new Set()).has(visitId));
      setPendingBulk({ type: 'col', id: visitId, label: visitName, allChecked, count: forms.length });
    },
    [assignments, forms],
  );

  const confirmBulk = useCallback(() => {
    if (!pendingBulk) return;
    if (pendingBulk.type === 'row') toggleRow(pendingBulk.id);
    else toggleColumn(pendingBulk.id);
    setPendingBulk(null);
  }, [pendingBulk, toggleRow, toggleColumn]);

  // ── Suggest assignments (from protocol analysis schedule) ────

  const [suggesting, setSuggesting] = useState(false);

  // One rule: the same matcher that fills the schedule (wording rule in
  // visitFormMatch.ts) over the labels each visit carries, so a suggestion never
  // contradicts the schedule. It only ticks; nothing is saved until Save.
  const [suggestedWording, setSuggestedWording] = useState<Map<string, Map<string, string>>>(new Map());
  const wordingCells = useMemo(() => {
    const merged = wordingMatchedCells(visits, forms);
    suggestedWording.forEach((byVisit, formId) => {
      if (!merged.has(formId)) merged.set(formId, new Map());
      byVisit.forEach((label, visitId) => merged.get(formId)!.set(visitId, label));
    });
    return merged;
  }, [visits, forms, suggestedWording]);

  const suggestAssignments = useCallback(async () => {
    setSuggesting(true);
    try {
      const s = suggestAssignmentsFromSchedule(visits, forms);
      setAssignments((prev) => {
        const next = new Map(prev);
        s.assignments.forEach((set, formId) => {
          const cur = new Set(next.get(formId) || []);
          set.forEach((visitId) => cur.add(visitId));
          next.set(formId, cur);
        });
        return next;
      });
      setSuggestedWording(s.byWording);
      showToast(
        s.ticks > 0
          ? `Suggested ${s.ticks} assignments from the schedule. Review them, then Save.`
          : 'No schedule item matched a form, so nothing was suggested',
        'info',
      );
    } catch (err) {
      console.error('VisitScheduleMatrix: suggest error', err);
      showToast('Could not suggest assignments', 'error');
    } finally {
      setSuggesting(false);
    }
  }, [forms, visits, showToast]);

  // ── Update from protocol ────

  const updateFromProtocol = useCallback(async () => {
    try {
      // Re-read studyDesign/config for latest visits (already live via onSnapshot)
      // Merge new forms with category defaults, keep existing assignments
      setAssignments((prev) => {
        const next = new Map(prev);
        for (const f of forms) {
          if (!next.has(f.id) || (next.get(f.id)?.size || 0) === 0) {
            const cat = f.category.toLowerCase();
            if (cat === 'every-visit') {
              next.set(f.id, new Set(visits.map((v) => v.id)));
            } else if (
              cat === 'one-time' ||
              cat === 'screening' ||
              cat === 'demographics'
            ) {
              if (visits.length > 0) {
                next.set(f.id, new Set([visits[0].id]));
              }
            } else {
              next.set(f.id, new Set());
            }
          }
        }
        return next;
      });
      setProtocolStale(false);
      setProtocolDismissed(false);
      showToast('Visit schedule updated from latest protocol', 'success');
    } catch (err) {
      showToast('Failed to update from protocol', 'error');
    }
  }, [forms, visits, showToast]);

  // ── Save ────

  // Which export format is currently downloading ('csv' | 'odm' | 'docx'), or null.
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    if (!projectId || saving || !mappingVersion) return;
    setSaving(true);
    setStaleArtifacts([]);

    try {
      // Fetch protocolAnalysis/latest.updatedAt for timestamp
      let protocolAnalysisTimestamp: any = undefined;
      try {
        const analysisSnap = await getDoc(
          protocolAnalysisLatestDoc(projectId),
        );
        if (analysisSnap.exists()) {
          protocolAnalysisTimestamp = analysisSnap.data().updatedAt || null;
        }
      } catch {
        // non-critical
      }

      const mappingDoc: any = {
        visits: visits.map((v) => ({
          id: v.id,
          name: v.name,
          ...(ownWindowText(v) !== undefined ? { window: ownWindowText(v) as string } : {}),
          forms: forms
            .filter((f) => assignments.get(f.id)?.has(v.id))
            .map((f) => f.id),
        })),
        form_inventory: forms.map((f) => ({
          id: f.id,
          name: f.name,
          layout: 'question-list',
          category: f.category,
        })),
        editedAt: new Date(),
        editedBy: getAuth().currentUser?.uid || 'unknown',
        protocolAnalysisTimestamp,
      };

      // Preserve existing fields if they exist
      if (savedMapping?.study) mappingDoc.study = savedMapping.study;
      if (savedMapping?.generatedAt) mappingDoc.generatedAt = savedMapping.generatedAt;

      // The whole mapping, from this page's copy: claims the version the copy
      // came from plus one, and is refused if the stored mapping has moved.
      const outcome = await mappingVersion.write((next) =>
        setDoc(crfPackageVisitMappingDoc(projectId), { ...mappingDoc, docVersion: next }),
      );
      if (outcome !== 'saved') {
        showToast(docChangedMessage('The form assignments'), 'error');
        return;
      }

      // Update saved state
      setSavedMapping(mappingDoc);
      setSavedSnapshot(serializeAssignments(assignments));

      // Check for stale downstream artifacts (generated before this mapping was last edited)
      const mappingEditedAt = savedMapping?.editedAt
        ? toDate(savedMapping.editedAt)
        : null;
      try {
        const acrfSnap = await getDoc(
          artifactDoc(projectId, 'acrf'),
        );
        if (acrfSnap.exists()) {
          const acrfUpdated = toDate(acrfSnap.data().updatedAt);
          if (acrfUpdated && mappingEditedAt && acrfUpdated < mappingEditedAt) {
            setStaleArtifacts([
              { name: 'Annotated CRF', date: shortDate(acrfUpdated) },
            ]);
          }
        }
      } catch {
        // non-critical
      }

      showToast('Visit schedule saved', 'success');
    } catch (err) {
      console.error('VisitScheduleMatrix: save error', err);
      showToast(`Failed to save visit schedule: ${(err as Error).message}`, 'error');
    } finally {
      setSaving(false);
    }
  }, [projectId, saving, visits, forms, assignments, savedMapping, showToast, mappingVersion]);

  // Export the saved visit/form matrix. The backend reads the persisted
  // crfPackage/visitMapping doc, so this exports the last *saved* state and
  // returns a signed Storage URL we fetch and download (same pattern as the
  // SDTM panel).
  const runMatrixExport = useCallback(
    async (format: 'csv' | 'odm' | 'docx') => {
      if (!projectId || exportingFormat) return;
      setExportingFormat(format);
      try {
        const fn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_VISIT_FORM_MATRIX_V2, {
          timeout: CALLABLE_TIMEOUTS.MEDIUM,
        });
        const result = await fn({ projectId, format });
        const { downloadUrl, filename, mimeType } = result.data as {
          downloadUrl: string;
          filename: string;
          mimeType: string;
        };
        const resp = await fetch(downloadUrl);
        if (!resp.ok) throw new Error(`Download failed: ${resp.status} ${resp.statusText}`);
        const blob = await resp.blob();
        saveAs(new Blob([blob], { type: mimeType }), filename);
      } catch (err: any) {
        if (err?.code === 'functions/failed-precondition') {
          showToast('Assign forms to visits and Save the matrix before exporting.', 'error');
        } else {
          console.error('VisitScheduleMatrix: export error', err);
          showToast(err?.message || 'Failed to export the visit/form matrix', 'error');
        }
      } finally {
        setExportingFormat(null);
      }
    },
    [projectId, exportingFormat, showToast],
  );

  // ── Order forms to mirror the uploaded Schedule of Events ────
  //
  // When the SOE row order is known (scheduleFormOrder, captured at import),
  // render a flat list in that exact order. Forms not present in the SOE are
  // appended in their existing order. With no SOE order on file, fall back to
  // the forms' natural order.

  const orderedForms = useMemo(() => {
    if (!scheduleFormOrder || scheduleFormOrder.length === 0) return forms;
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const rankByName = new Map<string, number>();
    scheduleFormOrder.forEach((name, i) => {
      const k = norm(name);
      if (k && !rankByName.has(k)) rankByName.set(k, i);
    });
    const rank = (f: { name: string }) =>
      rankByName.has(norm(f.name)) ? rankByName.get(norm(f.name))! : Number.MAX_SAFE_INTEGER;
    return forms
      .map((f, i) => ({ f, i }))
      .sort((a, b) => {
        const ra = rank(a.f);
        const rb = rank(b.f);
        return ra !== rb ? ra - rb : a.i - b.i; // stable for unmatched forms
      })
      .map(({ f }) => f);
  }, [forms, scheduleFormOrder]);

  // ── Per-visit totals ────

  const visitTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const v of visits) {
      totals[v.id] = forms.filter((f) => assignments.get(f.id)?.has(v.id)).length;
    }
    return totals;
  }, [visits, forms, assignments]);

  const toConfirm = useMemo(() => formsToConfirmSummary(visits), [visits]);

  // ── Empty states ────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-3" />
        <span className="text-gray-500">Loading visit schedule...</span>
      </div>
    );
  }

  if (visits.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <p>No visits defined in Study Design yet.</p>
        <p className="text-sm mt-1">Add visits in the Study Design step, then return here.</p>
      </div>
    );
  }

  if (forms.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <p>No forms available.</p>
        <p className="text-sm mt-1">Generate or add forms in Forms View first.</p>
      </div>
    );
  }

  // ── Render ────

  return (
    <div className="space-y-3">
      {toConfirm.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg px-4 py-3 text-sm border bg-amber-50 border-amber-200">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-500" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">To be confirmed</p>
            <p className="text-amber-800">
              These labels from the protocol matched no form. Assign a form to each visit below.
            </p>
            <ul className="mt-1 list-disc pl-5 text-amber-900">
              {toConfirm.map((e) => (
                <li key={e.visitId}>
                  {e.visitName}: {e.labels.join(', ')}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {/* Protocol amendment banner */}
      {/* Schedule of Events review status (coverage map G9). Read-only: it
          reports the record, it does not create or change one. Deliberately
          not dismissable — a governance record that can be hidden is not one. */}
      {confirmationVerdict && (
        <div
          className={
            'flex items-start gap-3 rounded-lg px-4 py-3 text-sm border ' +
            (confirmationVerdict.state === 'CONFIRMED'
              ? 'bg-green-50 border-green-200'
              : confirmationVerdict.state === 'DRIFTED'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-slate-50 border-slate-200')
          }
        >
          {confirmationVerdict.state === 'CONFIRMED' ? (
            <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle
              className={
                'h-5 w-5 flex-shrink-0 mt-0.5 ' +
                (confirmationVerdict.state === 'DRIFTED' ? 'text-amber-500' : 'text-slate-400')
              }
            />
          )}
          <div className="flex-1">
            <p
              className={
                'font-medium ' +
                (confirmationVerdict.state === 'CONFIRMED'
                  ? 'text-green-800'
                  : confirmationVerdict.state === 'DRIFTED'
                    ? 'text-amber-800'
                    : 'text-slate-700')
              }
            >
              {confirmationVerdict.state === 'CONFIRMED'
                ? 'Schedule of Events reviewed and unchanged since'
                : confirmationVerdict.state === 'DRIFTED'
                  ? 'Schedule of Events has changed since it was reviewed'
                  : 'Schedule of Events has not been reviewed'}
            </p>
            <p
              className={
                'mt-0.5 ' +
                (confirmationVerdict.state === 'CONFIRMED'
                  ? 'text-green-700'
                  : confirmationVerdict.state === 'DRIFTED'
                    ? 'text-amber-700'
                    : 'text-slate-600')
              }
            >
              {confirmation?.confirmedBy
                ? `Reviewed by ${confirmation.confirmedBy}` +
                  (confirmation.confirmedAt
                    ? ` on ${new Date(confirmation.confirmedAt).toLocaleDateString()}`
                    : '') +
                  (typeof confirmation.visitCount === 'number'
                    ? `, covering ${confirmation.visitCount} visits.`
                    : '.')
                : 'No one has confirmed this schedule yet.'}
              {confirmationVerdict.state === 'DRIFTED' &&
                ' It should be reviewed again so the record matches what the study now holds.'}
            </p>
          </div>
        </div>
      )}

      {protocolStale && !protocolDismissed && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-amber-800 font-medium">
              Protocol has been updated since this visit schedule was saved.
            </p>
            <p className="text-amber-700 mt-0.5">
              Visit assignments may need changes.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={updateFromProtocol}
              className="px-3 py-1.5 text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-md flex items-center gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Update from Protocol
            </button>
            <button
              onClick={() => setProtocolDismissed(true)}
              className="px-3 py-1.5 text-sm text-amber-600 hover:bg-amber-100 rounded-md"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Stale artifact banner */}
      {staleArtifacts.length > 0 && (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm">
          <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-blue-800">
              Visit schedule saved. These artifacts may need regeneration:{' '}
              {staleArtifacts.map((a) => `${a.name} (last generated ${a.date})`).join(', ')}
            </p>
          </div>
          <button
            onClick={() => setStaleArtifacts([])}
            className="text-blue-400 hover:text-blue-600 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={suggestAssignments}
            disabled={suggesting}
            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-md flex items-center gap-1.5"
          >
            {suggesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {suggesting ? 'Suggesting...' : 'Suggest Assignments'}
          </button>
          <button
            onClick={() => runMatrixExport('csv')}
            disabled={exportingFormat !== null}
            title="Download the Schedule of Assessments as CSV"
            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-md flex items-center gap-1.5"
          >
            {exportingFormat === 'csv' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            CSV
          </button>
          <button
            onClick={() => runMatrixExport('odm')}
            disabled={exportingFormat !== null}
            title="Download as Medidata Rave ODM-XML"
            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-md flex items-center gap-1.5"
          >
            {exportingFormat === 'odm' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            ODM-XML
          </button>
          <button
            onClick={() => runMatrixExport('docx')}
            disabled={exportingFormat !== null}
            title="Download the Schedule of Assessments as a Word document"
            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-md flex items-center gap-1.5"
          >
            {exportingFormat === 'docx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Matrix (Word)
          </button>
        </div>

        <div className="flex items-center gap-3">
          {isDirty && (
            <span className="text-sm text-amber-600">
              {unsavedCount} unsaved change{unsavedCount !== 1 ? 's' : ''}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className={`px-4 py-1.5 text-sm rounded-md flex items-center gap-1.5 ${
              isDirty && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </button>
        </div>
      </div>

      {/* Bulk-action confirmation bar */}
      {pendingBulk && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
          <p className="text-sm text-amber-800">
            {pendingBulk.type === 'col'
              ? pendingBulk.allChecked
                ? <>Remove all <strong>{pendingBulk.count}</strong> forms from <strong>{pendingBulk.label}</strong>?</>
                : <>Assign all <strong>{pendingBulk.count}</strong> forms to <strong>{pendingBulk.label}</strong>?</>
              : pendingBulk.allChecked
                ? <>Remove <strong>{pendingBulk.label}</strong> from all <strong>{pendingBulk.count}</strong> visits?</>
                : <>Assign <strong>{pendingBulk.label}</strong> to all <strong>{pendingBulk.count}</strong> visits?</>}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={confirmBulk}
              className="px-3 py-1 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
            >
              Confirm
            </button>
            <button
              onClick={() => setPendingBulk(null)}
              className="px-3 py-1 text-sm font-medium text-amber-700 bg-white border border-amber-300 rounded-md hover:bg-amber-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
        <div
          className="min-w-max"
          style={{
            display: 'grid',
            gridTemplateColumns: `200px repeat(${visits.length}, 80px)`,
          }}
        >
          {/* ── Column headers ── */}
          <div className="sticky left-0 z-10 bg-gray-50 border-b border-r border-gray-200 px-3 py-2 font-medium text-sm text-gray-700">
            Form
          </div>
          {visits.map((v) => (
            <button
              key={`hdr-${v.id}`}
              onClick={() => requestToggleColumn(v.id, v.name)}
              className="bg-gray-50 border-b border-r border-gray-200 px-1 py-2 text-center hover:bg-amber-50 cursor-pointer"
              title={`Select / deselect all forms for ${v.name}`}
            >
              <div className="text-xs font-semibold text-gray-700 truncate">
                {v.name}
              </div>
              <div className="text-[10px] text-gray-400">{typeof v.day === 'number' ? `Day ${v.day}` : 'Day TBD'}</div>
            </button>
          ))}

          {/* ── Form rows in Schedule-of-Events order (flat) ── */}
          {orderedForms.map((f) => (
            <React.Fragment key={`row-${f.id}`}>
              {/* Form name cell (sticky) */}
              <button
                onClick={() => requestToggleRow(f.id, f.name)}
                className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-3 py-2 text-left text-sm text-gray-800 hover:bg-amber-50 truncate cursor-pointer"
                title={`Select / deselect all visits for ${f.name}`}
              >
                {f.name}
              </button>

              {/* Checkbox cells */}
              {visits.map((v) => {
                const checked = assignments.get(f.id)?.has(v.id) || false;
                return (
                  <button
                    key={`cell-${f.id}-${v.id}`}
                    role="checkbox"
                    aria-checked={checked}
                    aria-label={`${f.name} at ${v.name}`}
                    title={
                      checked && wordingCells.get(f.id)?.has(v.id)
                        ? `Matched by wording from the schedule item "${wordingCells.get(f.id)!.get(v.id)}". Click to untick.`
                        : undefined
                    }
                    onClick={() => toggleCell(f.id, v.id)}
                    className={`border-b border-r border-gray-200 flex items-center justify-center cursor-pointer transition-colors ${
                      checked
                        ? `bg-blue-500 hover:bg-blue-600 ${wordingCells.get(f.id)?.has(v.id) ? 'ring-2 ring-inset ring-amber-300' : ''}`
                        : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    {checked ? (
                      <Check className="h-4 w-4 text-white" />
                    ) : (
                      <div className="w-4 h-4 rounded border border-gray-300" />
                    )}
                  </button>
                );
              })}
            </React.Fragment>
          ))}

          {/* ── Totals row ── */}
          <div className="sticky left-0 z-10 bg-gray-50 border-r border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600">
            Forms per visit
          </div>
          {visits.map((v) => (
            <div
              key={`total-${v.id}`}
              className="bg-gray-50 border-r border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600"
            >
              {visitTotals[v.id] || 0}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
